import { useState, type ComponentPropsWithoutRef, type ComponentRef, type Ref } from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import cn from '@components/utils/cn'

const Tabs = ({
  urlKey = 'tab',
  activationMode = 'manual',
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
  urlKey?: string
  ref?: Ref<ComponentRef<typeof TabsPrimitive.Root>>
}) => {
  const defaultValue = new URLSearchParams(document.location.search).get(urlKey) ?? props.defaultValue
  const [value, setValue] = useState(defaultValue)
  const onValueChange = (newValue: string) => {
    if (props.onValueChange) {
      props.onValueChange(newValue)
    } else {
      setValue(newValue)
      // add value to URL without reloading page
      const urlParams = new URLSearchParams(document.location.search)
      urlParams.delete(urlKey)
      urlParams.set(urlKey, newValue)
      history.replaceState(history.state, document.title, `?${urlParams.toString()}`)
    }
  }

  return (
    <TabsPrimitive.Root
      ref={ref}
      {...props}
      activationMode={activationMode}
      onValueChange={onValueChange}
      value={value}
    />
  )
}

const TabsList = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  ref?: Ref<ComponentRef<typeof TabsPrimitive.List>>
}) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'tw:inline-flex tw:items-center tw:justify-center tw:rounded-lg tw:border-gray-300 tw:text-gray-700',
      className
    )}
    {...props}
  />
)

const TabsTrigger = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
  ref?: Ref<ComponentRef<typeof TabsPrimitive.Trigger>>
}) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'tw:relative tw:inline-flex tw:items-center tw:justify-center tw:whitespace-nowrap tw:px-5 tw:py-3 tw:text-sm tw:font-medium tw:transition-all',
      'tw:last-of-type:rounded-r-lg tw:first-of-type:rounded-l-lg',
      'tw:border-gray-300 tw:border tw:last-of-type:mr-0 tw:-mr-px',
      'tw:focus-visible:outline-hidden tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300 tw:focus-visible:z-10',
      'tw:disabled:pointer-events-none tw:disabled:opacity-50',
      'tw:data-[state=active]:bg-primary-500 tw:data-[state=active]:text-white',
      className
    )}
    {...props}
  />
)

const TabsContent = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {
  ref?: Ref<ComponentRef<typeof TabsPrimitive.Content>>
}) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('tw:focus-visible:outline-hidden tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300', className)}
    {...props}
  />
)

export { Tabs, TabsList, TabsTrigger, TabsContent }
